import React, { useState, useEffect, ReactNode, useContext } from "react";
import { resolveOrgForLogin } from "@shared/services/resolveOrgClient";
import logger from "@shared/utils/logger";
import { getOrganizationLookupInputFromHostname } from "@shared/utils/orgLoginRouting";
import {
  DEFAULT_SETTINGS,
  SettingsContext,
} from "@shared/contexts/SettingsContext";
import { ThemeContext } from "./ThemeContext";

export type ThemeMode = "light" | "dark" | "high-contrast";
export type ColorBlindMode =
  | "none"
  | "protanopia"
  | "deuteranopia"
  | "tritanopia";
export type FontSize = "small" | "medium" | "large" | "xlarge";

export interface OrganizationBranding {
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
  favicon_url?: string | null;
  login_bg_url?: string | null;
  font_family?: string | null;
  custom_css?: string | null;
  support_email?: string | null;
}

export interface ThemeContextType {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  colorBlindMode: ColorBlindMode;
  setColorBlindMode: (mode: ColorBlindMode) => void;
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  branding: OrganizationBranding | null;
  refreshBranding: () => Promise<void>;
}

export interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: ThemeMode;
  initialFontSize?: FontSize;
  initialColorBlind?: ColorBlindMode | "normal";
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
}

const THEME_STORAGE_KEY = "eeg-theme";
const COLOR_BLIND_STORAGE_KEY = "eeg-colorblind-mode";
const FONT_SIZE_STORAGE_KEY = "eeg-font-size";

function getStorage(
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null,
) {
  if (storage) {
    return storage;
  }

  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    return null;
  }

  return null;
}

function readStoredTheme(
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null,
): ThemeMode {
  const storageTarget = getStorage(storage);
  const storedTheme = storageTarget?.getItem(THEME_STORAGE_KEY);

  if (
    storedTheme === "light" ||
    storedTheme === "dark" ||
    storedTheme === "high-contrast"
  ) {
    return storedTheme;
  }

  if (storedTheme !== null && storedTheme !== undefined) {
    return "light";
  }

  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }

  return "light";
}

function readStoredFontSize(
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null,
): FontSize {
  return normalizeFontSize(getStorage(storage)?.getItem(FONT_SIZE_STORAGE_KEY));
}

function readStoredColorBlindMode(
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null,
): ColorBlindMode {
  return normalizeColorBlindMode(
    getStorage(storage)?.getItem(COLOR_BLIND_STORAGE_KEY),
  );
}

function normalizeThemeMode(theme: unknown, highContrast: unknown): ThemeMode {
  if (highContrast === true) return "high-contrast";
  if (theme === "dark") return "dark";
  return "light";
}

function normalizeFontSize(value: unknown): FontSize {
  if (value === "small" || value === "medium" || value === "large") {
    return value;
  }
  if (value === "xlarge" || value === "extra-large") {
    return "xlarge";
  }
  return "medium";
}

function normalizeColorBlindMode(value: unknown): ColorBlindMode {
  if (
    value === "none" ||
    value === "protanopia" ||
    value === "deuteranopia" ||
    value === "tritanopia"
  ) {
    return value;
  }
  if (value === "normal") {
    return "none";
  }
  return "none";
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialFontSize,
  initialTheme,
  initialColorBlind,
  storage,
}) => {
  const settingsContext = useContext(SettingsContext);
  const hasSettingsProvider = settingsContext !== undefined;
  const settings = settingsContext?.settings ?? DEFAULT_SETTINGS;
  const settingsLoading = settingsContext?.loading ?? false;
  const updateGeneralSettings =
    settingsContext?.updateGeneralSettings ??
    (async () => DEFAULT_SETTINGS);
  const storageTarget = getStorage(storage);

  // Local overrides preserve immediate responsiveness and support unit tests
  // that mount ThemeProvider without SettingsProvider.
  const [themeOverride, setThemeOverride] = useState<ThemeMode | null>(
    initialTheme ?? (hasSettingsProvider ? null : readStoredTheme(storage)),
  );
  const [fontSizeOverride, setFontSizeOverride] = useState<FontSize | null>(
    initialFontSize ??
      (hasSettingsProvider ? null : readStoredFontSize(storage)),
  );
  const [colorBlindOverride, setColorBlindOverride] = useState<ColorBlindMode | null>(
    initialColorBlind
      ? normalizeColorBlindMode(initialColorBlind)
      : hasSettingsProvider
        ? null
        : readStoredColorBlindMode(storage),
  );

  const [branding, setBranding] = useState<OrganizationBranding | null>(null);

  const themeFromSettings = normalizeThemeMode(
    settings.theme,
    settings.high_contrast,
  );
  const fontSizeFromSettings = normalizeFontSize(settings.font_size);
  const colorBlindFromSettings = normalizeColorBlindMode(
    settings.color_vision_mode,
  );

  const theme = themeOverride ?? themeFromSettings;
  const fontSize = fontSizeOverride ?? fontSizeFromSettings;
  const colorBlindMode = colorBlindOverride ?? colorBlindFromSettings;

  const persistGeneralSettings = async (patch: Record<string, unknown>) => {
    try {
      await updateGeneralSettings(patch);
    } catch (error) {
      logger.error("[ThemeProvider] Failed to persist settings", error as Error);
    }
  };

  const setTheme = (newTheme: ThemeMode) => {
    logger.debug(`[ThemeProvider] setTheme -> ${newTheme}`);
    setThemeOverride(newTheme);
    const isHighContrast = newTheme === "high-contrast";
    void persistGeneralSettings({
      theme: isHighContrast ? "light" : newTheme,
      high_contrast: isHighContrast,
    });
  };

  const toggleTheme = () => {
    const next =
      theme === "light"
        ? "dark"
        : theme === "dark"
          ? "high-contrast"
          : "light";
    setTheme(next);
  };

  const setColorBlindMode = (mode: ColorBlindMode) => {
    logger.debug(`[ThemeProvider] setColorBlindMode -> ${mode}`);
    setColorBlindOverride(mode);
    void persistGeneralSettings({ color_vision_mode: mode });
  };

  const setFontSize = (size: FontSize) => {
    logger.debug(`[ThemeProvider] setFontSize -> ${size}`);
    setFontSizeOverride(size);
    void persistGeneralSettings({ font_size: size });
  };

  useEffect(() => {
    if (
      hasSettingsProvider ||
      settingsLoading ||
      settings !== DEFAULT_SETTINGS ||
      themeOverride !== null ||
      initialTheme
    ) {
      return;
    }

    setThemeOverride(readStoredTheme(storage));
  }, [
    hasSettingsProvider,
    initialTheme,
    settings,
    settingsLoading,
    storage,
    themeOverride,
  ]);

  useEffect(() => {
    if (
      hasSettingsProvider ||
      settingsLoading ||
      settings !== DEFAULT_SETTINGS ||
      fontSizeOverride !== null ||
      initialFontSize
    ) {
      return;
    }

    setFontSizeOverride(readStoredFontSize(storage));
  }, [
    fontSizeOverride,
    hasSettingsProvider,
    initialFontSize,
    settings,
    settingsLoading,
    storage,
  ]);

  useEffect(() => {
    if (
      hasSettingsProvider ||
      settingsLoading ||
      settings !== DEFAULT_SETTINGS ||
      colorBlindOverride !== null ||
      initialColorBlind
    ) {
      return;
    }

    setColorBlindOverride(readStoredColorBlindMode(storage));
  }, [
    colorBlindOverride,
    hasSettingsProvider,
    initialColorBlind,
    settings,
    settingsLoading,
    storage,
  ]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.setAttribute(
      "data-bs-theme",
      theme === "dark" ? "dark" : "light",
    );
    document.documentElement.setAttribute("data-font-size", fontSize);
    document.documentElement.setAttribute(
      "data-high-contrast",
      String(theme === "high-contrast"),
    );

    if (colorBlindMode === "none") {
      document.documentElement.removeAttribute("data-color-blind-mode");
    } else {
      document.documentElement.setAttribute(
        "data-color-blind-mode",
        colorBlindMode,
      );
    }

    document.body.classList.remove(
      "theme-light",
      "theme-dark",
      "theme-high-contrast",
    );
    document.body.classList.add(`theme-${theme}`);

    document.body.classList.remove("light-theme", "dark-theme");
    if (theme === "dark") {
      document.body.classList.add("dark-theme");
    } else {
      document.body.classList.add("light-theme");
    }

    document.body.classList.remove(
      "font-size-small",
      "font-size-medium",
      "font-size-large",
      "font-size-xlarge",
      "font-small",
      "font-medium",
      "font-large",
    );
    document.body.classList.add(`font-size-${fontSize}`);
    document.body.classList.add(
      fontSize === "xlarge" ? "font-large" : `font-${fontSize}`,
    );

    document.body.classList.remove(
      "colorblind-protanopia",
      "colorblind-deuteranopia",
      "colorblind-tritanopia",
    );
    if (colorBlindMode !== "none") {
      document.body.classList.add(`colorblind-${colorBlindMode}`);
    }
  }, [colorBlindMode, fontSize, theme]);

  useEffect(() => {
    if (!storageTarget) {
      return;
    }

    try {
      storageTarget.setItem(THEME_STORAGE_KEY, theme);
      storageTarget.setItem(
        COLOR_BLIND_STORAGE_KEY,
        colorBlindMode === "none" ? "normal" : colorBlindMode,
      );
      storageTarget.setItem(FONT_SIZE_STORAGE_KEY, fontSize);
    } catch {
      // Ignore storage errors for non-critical UI preferences.
    }
  }, [colorBlindMode, fontSize, storageTarget, theme]);

  // Clear local overrides once backend-backed settings converge.
  useEffect(() => {
    if (
      hasSettingsProvider &&
      themeOverride !== null &&
      themeOverride === themeFromSettings
    ) {
      setThemeOverride(null);
    }
  }, [hasSettingsProvider, themeFromSettings, themeOverride]);

  useEffect(() => {
    if (
      hasSettingsProvider &&
      fontSizeOverride !== null &&
      fontSizeOverride === fontSizeFromSettings
    ) {
      setFontSizeOverride(null);
    }
  }, [fontSizeFromSettings, fontSizeOverride, hasSettingsProvider]);

  useEffect(() => {
    if (
      hasSettingsProvider &&
      colorBlindOverride !== null &&
      colorBlindOverride === colorBlindFromSettings
    ) {
      setColorBlindOverride(null);
    }
  }, [colorBlindFromSettings, colorBlindOverride, hasSettingsProvider]);

  const applyBranding = (brandingData: OrganizationBranding) => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;

    if (brandingData.primary_color) {
      root.style.setProperty("--brand-primary", brandingData.primary_color);
    }
    if (brandingData.secondary_color) {
      root.style.setProperty("--brand-secondary", brandingData.secondary_color);
    }
    if (brandingData.accent_color) {
      root.style.setProperty("--brand-accent", brandingData.accent_color);
    }
    if (brandingData.font_family) {
      root.style.setProperty("--brand-font-family", brandingData.font_family);
      document.body.style.fontFamily = `var(--brand-font-family), var(--font-family-base, system-ui, sans-serif)`;
    }

    const customCss = brandingData.custom_css?.trim();
    const hasBalancedBraces = customCss
      ? [...customCss].reduce(
          (depth, character) =>
            depth < 0
              ? depth
              : character === "{"
                ? depth + 1
                : character === "}"
                  ? depth - 1
                  : depth,
          0,
        ) === 0 && !customCss.startsWith("}")
      : false;
    if (customCss && hasBalancedBraces) {
      let styleEl = document.getElementById("brand-custom-css");
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = "brand-custom-css";
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = customCss;
    }

    if (brandingData.favicon_url) {
      let favicon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.href = brandingData.favicon_url;
    }

    if (brandingData.login_bg_url) {
      root.style.setProperty("--brand-login-bg", `url(${brandingData.login_bg_url})`);
    }
  };

  const fetchBranding = async () => {
    try {
      const hostname = window.location.hostname;
      const lookupInput = getOrganizationLookupInputFromHostname(hostname);

      if (!lookupInput) {
        return;
      }

      const response = await resolveOrgForLogin(lookupInput, {
        cacheTtlMs: 60_000,
      });
      const organization = response?.organization;
      if (!organization) {
        return;
      }

      const brandingData = {
        logo_url: organization.logo_url ?? null,
        primary_color: organization.primary_color ?? null,
        secondary_color: organization.secondary_color ?? null,
        accent_color: organization.accent_color ?? null,
        favicon_url: organization.favicon_url ?? null,
        login_bg_url: organization.login_bg_url ?? null,
        font_family: organization.font_family ?? null,
        custom_css: organization.custom_css ?? null,
        support_email: organization.support_email ?? null,
      };
      setBranding(brandingData);
      applyBranding(brandingData);
    } catch (error) {
      logger.error("[ThemeProvider] Failed to fetch branding", error as Error);
    }
  };

  const refreshBranding = async () => {
    await fetchBranding();
  };

  useEffect(() => {
    fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
    colorBlindMode,
    setColorBlindMode,
    fontSize,
    setFontSize,
    branding,
    refreshBranding,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
